/**
 * FieldSensitivitySettings Component
 * Manages field-level sensitivity defaults and per-program overrides.
 *
 * Section 1: System Defaults Table — all default field sensitivities
 * Section 2: Program Selector — pick a program, load its overrides
 * Section 3: Program Field Overrides — override tiers for PII/PHI fields
 */

import { useTabbedChrome } from '@/components/weavein';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Save,
  ChevronDown,
  Table2,
  Lock,
  Eye,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { useTranslation } from 'react-i18next';
import {
  privacyAPI,
  FieldSensitivityDefault,
  FieldSensitivityOverride,
  SensitivityTier,
  SENSITIVITY_TIERS,
  TIER_LABELS,
  ProgramPrivacySummaryItem,
} from '@/services/api/privacy';

// ============================================================================
// PROPS
// ============================================================================

interface FieldSensitivitySettingsProps {
  organizationId: number;
  organizationName: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/** Convert snake_case field name to Title Case (e.g. "date_of_birth" -> "Date Of Birth") */
function formatFieldName(name: string): string {
  return name
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Map a sensitivity tier to its Tailwind badge classes */
function tierBadgeClasses(tier: SensitivityTier): string {
  switch (tier) {
    case 'public':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    case 'internal':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    case 'confidential':
      return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    case 'restricted':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  }
}

// ============================================================================
// LOCAL TYPES
// ============================================================================

/** Editable row state for a single program field override */
interface OverrideRow {
  field_name: string;
  table_name: string;
  default_tier: SensitivityTier;
  override_tier: SensitivityTier | null; // null = "Inherit"
  override_reason: string;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function FieldSensitivitySettings({
  organizationId,
  organizationName,
}: FieldSensitivitySettingsProps) {
  const tabbed = useTabbedChrome();
  const { t } = useTranslation();

  // --- Data state ---
  const [defaults, setDefaults] = useState<FieldSensitivityDefault[]>([]);
  const [programs, setPrograms] = useState<ProgramPrivacySummaryItem[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [overrideRows, setOverrideRows] = useState<OverrideRow[]>([]);

  // --- UI state ---
  const [loadingDefaults, setLoadingDefaults] = useState(true);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --------------------------------------------------------------------------
  // DATA FETCHING
  // --------------------------------------------------------------------------

  const fetchDefaults = useCallback(async () => {
    setLoadingDefaults(true);
    try {
      const data = await privacyAPI.getFieldSensitivityDefaults(organizationId);
      setDefaults(data);
    } catch (err: any) {
      console.error('Error fetching field sensitivity defaults:', err);
      setError('Failed to load field sensitivity defaults.');
    } finally {
      setLoadingDefaults(false);
    }
  }, [organizationId]);

  const fetchPrograms = useCallback(async () => {
    setLoadingPrograms(true);
    try {
      const data = await privacyAPI.getProgramsPrivacySummary(organizationId);
      setPrograms(data);
    } catch (err: any) {
      console.error('Error fetching programs privacy summary:', err);
      setError('Failed to load programs.');
    } finally {
      setLoadingPrograms(false);
    }
  }, [organizationId]);

  const fetchProgramOverrides = useCallback(
    async (programId: number) => {
      setLoadingOverrides(true);
      setError(null);
      try {
        const response = await privacyAPI.getProgramFieldSensitivity(
          organizationId,
          programId
        );

        // Build override rows from defaults that are PII or PHI
        const sensitiveDefaults = defaults.filter((d) => d.is_pii || d.is_phi);
        const existingOverrides = response.overrides;

        const rows: OverrideRow[] = sensitiveDefaults.map((def) => {
          const existing = existingOverrides.find(
            (o) => o.field_name === def.field_name && o.table_name === def.table_name
          );
          return {
            field_name: def.field_name,
            table_name: def.table_name,
            default_tier: def.tier_key,
            override_tier: existing ? existing.sensitivity_tier : null,
            override_reason: existing?.override_reason ?? '',
          };
        });

        setOverrideRows(rows);
      } catch (err: any) {
        console.error('Error fetching program field overrides:', err);
        setError('Failed to load program field overrides.');
      } finally {
        setLoadingOverrides(false);
      }
    },
    [organizationId, defaults]
  );

  // --- Initial load ---
  useEffect(() => {
    fetchDefaults();
    fetchPrograms();
  }, [fetchDefaults, fetchPrograms]);

  // --- Load overrides when program selection changes ---
  useEffect(() => {
    if (selectedProgramId !== null && defaults.length > 0) {
      fetchProgramOverrides(selectedProgramId);
    } else {
      setOverrideRows([]);
    }
  }, [selectedProgramId, defaults, fetchProgramOverrides]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  const handleProgramChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedProgramId(value ? parseInt(value, 10) : null);
    setSuccess(null);
    setError(null);
  };

  const handleOverrideTierChange = (index: number, value: string) => {
    setOverrideRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        override_tier: value === 'inherit' ? null : (value as SensitivityTier),
      };
      return next;
    });
  };

  const handleOverrideReasonChange = (index: number, value: string) => {
    setOverrideRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        override_reason: value,
      };
      return next;
    });
  };

  const handleSaveOverrides = async () => {
    if (selectedProgramId === null) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Only send rows that have an actual override (not "Inherit")
      const overridesToSave: FieldSensitivityOverride[] = overrideRows
        .filter((row) => row.override_tier !== null)
        .map((row) => ({
          field_name: row.field_name,
          table_name: row.table_name,
          sensitivity_tier: row.override_tier as SensitivityTier,
          custom_mask_pattern: null,
          override_reason: row.override_reason.trim() || null,
        }));

      await privacyAPI.setProgramFieldSensitivity(
        organizationId,
        selectedProgramId,
        overridesToSave
      );

      setSuccess('Program field sensitivity overrides saved successfully.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      const detail = err.response?.data?.detail || 'Failed to save overrides.';
      setError(detail);
      setTimeout(() => setError(null), 6000);
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // RENDER: LOADING STATE
  // --------------------------------------------------------------------------

  if (loadingDefaults && loadingPrograms) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500 mr-2" />
        <span className="text-gray-600 dark:text-gray-400">
          {t('fieldSensitivity.loading', 'Loading field sensitivity settings...')}
        </span>
      </div>
    );
  }

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  const selectedProgram = programs.find((p) => p.program_id === selectedProgramId);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* ------------------------------------------------------------------ */}
      {/* HEADER                                                              */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex items-center gap-3">
        {!tabbed && <Shield className="w-7 h-7 text-blue-600 dark:text-blue-400" />}
        <div>
          {!tabbed && (
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('fieldSensitivity.title', 'Field Privacy Configuration')}
          </h1>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t(
              'fieldSensitivity.subtitle',
              'Manage default field sensitivity tiers and per-program overrides for {{orgName}}',
              { orgName: organizationName }
            )}
          </p>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* STATUS MESSAGES                                                     */}
      {/* ------------------------------------------------------------------ */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SECTION 1: SYSTEM DEFAULTS TABLE                                    */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Table2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <CardTitle className="text-lg">
              {t('fieldSensitivity.defaultsTitle', 'System Field Sensitivity Defaults')}
            </CardTitle>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t(
              'fieldSensitivity.defaultsDescription',
              'The baseline sensitivity classification for each tracked field. Programs may override these on a per-field basis.'
            )}
          </p>
        </CardHeader>
        <CardContent>
          {loadingDefaults ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Loading defaults...
              </span>
            </div>
          ) : defaults.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
              No field sensitivity defaults found.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      Field Name
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      Table
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      Tier
                    </th>
                    <th className="text-center py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      PII
                    </th>
                    <th className="text-center py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      PHI
                    </th>
                    <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                      Mask Pattern
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {defaults.map((field) => (
                    <tr
                      key={`${field.table_name}.${field.field_name}`}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="py-2 px-3 font-medium text-gray-900 dark:text-gray-100">
                        {formatFieldName(field.field_name)}
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400 font-mono text-xs">
                        {field.table_name}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${tierBadgeClasses(field.tier_key)}`}
                        >
                          {TIER_LABELS[field.tier_key]}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        {field.is_pii ? (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <Eye className="w-3.5 h-3.5" />
                            Yes
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {field.is_phi ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
                            <Lock className="w-3.5 h-3.5" />
                            Yes
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">No</span>
                        )}
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                        {field.mask_pattern ?? (
                          <span className="text-gray-300 dark:text-gray-600">--</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* SECTION 2: PROGRAM SELECTOR                                         */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <CardTitle className="text-lg">
              {t('fieldSensitivity.programOverridesTitle', 'Program Field Overrides')}
            </CardTitle>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t(
              'fieldSensitivity.programOverridesDescription',
              'Select a program to view and configure field-level sensitivity overrides for PII and PHI fields.'
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Program dropdown */}
          <div>
            <label
              htmlFor="program-selector"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
            >
              {t('fieldSensitivity.selectProgram', 'Select Program')}
            </label>
            <div className="relative">
              <select
                id="program-selector"
                value={selectedProgramId ?? ''}
                onChange={handleProgramChange}
                disabled={loadingPrograms}
                className="w-full max-w-md appearance-none px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
              >
                <option value="">
                  {loadingPrograms ? 'Loading programs...' : '-- Choose a program --'}
                </option>
                {programs.map((prog) => (
                  <option key={prog.program_id} value={prog.program_id}>
                    {prog.program_name}
                    {prog.program_type ? ` (${prog.program_type})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* SECTION 3: PROGRAM FIELD OVERRIDES TABLE                         */}
          {/* ---------------------------------------------------------------- */}
          {selectedProgramId !== null && (
            <>
              {loadingOverrides ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-gray-400 mr-2" />
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Loading overrides for{' '}
                    {selectedProgram?.program_name ?? 'program'}...
                  </span>
                </div>
              ) : overrideRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                  No PII or PHI fields found in the defaults to override.
                </p>
              ) : (
                <div className="space-y-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Showing {overrideRows.length} PII/PHI field(s) for{' '}
                    <span className="font-medium text-gray-700 dark:text-gray-300">
                      {selectedProgram?.program_name ?? `Program #${selectedProgramId}`}
                    </span>
                    . Set each field to &quot;Inherit&quot; to use the system default, or
                    choose a specific tier to override.
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                            Field Name
                          </th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                            Default Tier
                          </th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                            Override Tier
                          </th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600 dark:text-gray-400">
                            Override Reason
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                        {overrideRows.map((row, index) => (
                          <tr
                            key={`${row.table_name}.${row.field_name}`}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                          >
                            {/* Field name */}
                            <td className="py-2.5 px-3 font-medium text-gray-900 dark:text-gray-100">
                              {formatFieldName(row.field_name)}
                            </td>

                            {/* Default tier (read-only badge) */}
                            <td className="py-2.5 px-3">
                              <span
                                className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${tierBadgeClasses(row.default_tier)}`}
                              >
                                {TIER_LABELS[row.default_tier]}
                              </span>
                            </td>

                            {/* Override tier dropdown */}
                            <td className="py-2.5 px-3">
                              <select
                                value={row.override_tier ?? 'inherit'}
                                onChange={(e) =>
                                  handleOverrideTierChange(index, e.target.value)
                                }
                                disabled={saving}
                                className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                              >
                                <option value="inherit">Inherit</option>
                                {SENSITIVITY_TIERS.map((tier) => (
                                  <option key={tier.key} value={tier.key}>
                                    {tier.label}
                                  </option>
                                ))}
                              </select>
                            </td>

                            {/* Override reason text input */}
                            <td className="py-2.5 px-3">
                              <input
                                type="text"
                                value={row.override_reason}
                                onChange={(e) =>
                                  handleOverrideReasonChange(index, e.target.value)
                                }
                                disabled={saving || row.override_tier === null}
                                placeholder={
                                  row.override_tier === null
                                    ? ''
                                    : 'Reason for override...'
                                }
                                className="w-full min-w-[180px] px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:bg-gray-50 dark:disabled:bg-gray-900 placeholder-gray-400"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Save button */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSaveOverrides}
                      disabled={saving}
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {saving ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          {t('fieldSensitivity.saving', 'Saving...')}
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          {t('fieldSensitivity.saveOverrides', 'Save Overrides')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
